import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Headphones, LogOut, Library, Sparkles } from "lucide-react";
import { useAudioAuth } from "./AudioAuthContext";
import { useAudioAccess } from "./AudioAccessContext";

export default function AudioLayout({ children }) {
  const { user, subscribed, logout } = useAudioAuth();
  const access = useAudioAccess();
  const { pathname } = useLocation();
  const showSubscribeCta = !access.is_free && !subscribed;

  const link = (href, label, icon) => (
    <Link to={href} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md transition ${pathname === href || pathname.startsWith(href + "/") ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
      {icon} {label}
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#0e0a14] text-white">
      <header className="sticky top-0 z-30 backdrop-blur bg-[#0e0a14]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid="ac-logo">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-fuchsia-500 to-amber-400 grid place-items-center shadow-lg group-hover:shadow-fuchsia-500/30 transition">
              <Headphones size={16} strokeWidth={2.4}/>
            </div>
            <div className="leading-tight">
              <div className="font-semibold text-base">Bala Basava Audio</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Stories · Vachanas</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {link("/audio-comics/books", "Library", <Library size={14}/>)}
            {showSubscribeCta && user && link("/audio-comics/subscription", "Go Pro", <Sparkles size={14}/>)}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                {subscribed ? (
                  <span className="hidden sm:inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30" data-testid="ac-pro-badge">
                    <Sparkles size={10}/> PRO
                  </span>
                ) : showSubscribeCta ? (
                  <Link to="/audio-comics/subscription" className="hidden sm:inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-amber-400 text-black font-semibold hover:bg-amber-300" data-testid="ac-cta-pro">
                    {access.cta_secondary}
                  </Link>
                ) : null}
                <div className="text-xs text-white/60 max-w-[120px] truncate hidden sm:block">{user.name || user.email}</div>
                <button onClick={logout} className="text-white/60 hover:text-white" title="Sign out" data-testid="ac-logout">
                  <LogOut size={16}/>
                </button>
              </>
            ) : access.is_free ? (
              <span className="hidden sm:inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">
                <Sparkles size={10}/> FREE
              </span>
            ) : (
              <Link to="/audio-comics/login" className="text-sm px-4 py-2 rounded-full bg-white text-black font-medium hover:bg-white/90" data-testid="ac-signin">Sign in</Link>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
      <footer className="border-t border-white/5 mt-12 px-4 sm:px-6 py-8 text-[11px] text-white/50" data-testid="audio-footer">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 gap-6">
          <div>
            <div className="text-white/80 font-semibold text-[13px] mb-1">ಬಾಲ ಬಸವ · BALA BASAVA MOVEMENT</div>
            <p className="leading-relaxed text-white/55 max-w-md">
              Basavanna's 12th-century vachanas, placed in children's hands — in the tongue they dream in.
            </p>
            <p className="mt-2 text-[10px] text-white/40">
              Bala Basava is an educational publishing initiative under <span className="text-white/60">Analytical and Creative Studio OPC Pvt Ltd</span>.
            </p>
          </div>
          <div className="sm:text-right">
            <div className="text-white/80 font-semibold text-[13px] mb-1">Contact Info</div>
            <div className="text-white/55 leading-relaxed">
              <div>Analytical and Creative Studio OPC Pvt Ltd</div>
              <div>3468 1st G Cross 7th Main RPC Layout</div>
              <div>Vijayanagar Bangalore 560040</div>
              <a href="mailto:info@balabasava.com" className="block hover:text-white/85 mt-2">info@balabasava.com</a>
              <a href="tel:+919164234566" className="block hover:text-white/85">+91 9164234566</a>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-6 pt-4 border-t border-white/5 text-center text-white/40">
          © {new Date().getFullYear()} Bala Basava Movement
        </div>
      </footer>
    </div>
  );
}
